import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Film, Clock, MoreVertical, Trash2, Edit3, Play, LogOut, User, Loader2, Wand2, Settings, Image as ImageIcon, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { useProjects, useDeleteProject, useCreateProject } from '../../hooks/useProjects';
import { useGenerateVideo } from '../../hooks/useGenerateVideo';
import { Button } from '../../components/atoms/Button';
import { Card } from '../../components/atoms/Card';
import { useAuthStore } from '../../stores/authStore';
import { OnboardingWizard, type OnboardingData } from '../onboarding/OnboardingWizard';
import type { Project } from '../../types/database';
import { supabase } from '../../lib/supabase';

export function ProjectsList() {
  const navigate = useNavigate();
  const { data: projects, isLoading, error } = useProjects();
  const deleteProject = useDeleteProject();
  const createProject = useCreateProject();
  const generateVideo = useGenerateVideo();
  const { signOut, user } = useAuthStore();
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [projectThumbnails, setProjectThumbnails] = useState<Record<string, string>>({});
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState<Project | null>(null);

  const [showOnboarding, setShowOnboarding] = useState(false);
  const [isGeneratingVideo, setIsGeneratingVideo] = useState(false);
  const [generationStatus, setGenerationStatus] = useState('');

  const handleCreateProject = () => {
    setShowOnboarding(true);
  };

  const handleOnboardingComplete = async (onboardingData: OnboardingData) => {
    setIsGeneratingVideo(true);
    setGenerationStatus('Kreiranje projekta...');

    try {
      console.log('[ProjectsList] Starting onboarding completion...');

      // Step 1: Create project
      console.log('[ProjectsList] Creating project...');
      const newProject = await createProject.mutateAsync({
        title: onboardingData.title,
        config: {
          target_duration_min: onboardingData.targetDurationMin,
          purpose: onboardingData.purpose,
          target_audience: onboardingData.targetAudience,
          tone: onboardingData.tone,
          include_narration: onboardingData.includeNarration,
          include_background_music: onboardingData.backgroundMusicMode !== 'none',
          background_music_mode: onboardingData.backgroundMusicMode,
          music_genre: onboardingData.musicGenre,
          ai_generated_prompt: null,
          user_custom_prompt: onboardingData.userCustomPrompt,
          metadata: {},
        },
      });
      console.log('[ProjectsList] Project created:', newProject.id);

      // Step 2: Generate video content with AI
      console.log('[ProjectsList] Generating video content...');
      const result = await generateVideo.mutateAsync({
        projectId: newProject.id,
        onboardingData,
        onStatusChange: setGenerationStatus,
      });
      console.log('[ProjectsList] Video generated:', result);

      // Step 3: Invalidate projects query to refresh the list
      console.log('[ProjectsList] Invalidating queries...');

      // Step 4: Navigate to editor with the generated content
      console.log('[ProjectsList] Navigating to editor...');
      setShowOnboarding(false);
      setIsGeneratingVideo(false);
      setGenerationStatus('');
      navigate(`/editor/${newProject.id}`);
    } catch (err) {
      console.error('[ProjectsList] Failed to create and generate video:', err);
      setIsGeneratingVideo(false);
      setGenerationStatus('');
      // Show error to user
      toast.error('Greška pri generiranju videa. Molimo pokušajte ponovo.');
    }
  };

  const handleSelectProject = (projectId: string) => {
    // Always navigate to editor - it will handle empty state
    navigate(`/editor/${projectId}`);
  };

  const handleDelete = async (projectId: string) => {
    // Find the project to get its title for the modal
    const project = projects?.find(p => p.id === projectId);
    if (project) {
      setProjectToDelete(project);
      setDeleteModalOpen(true);
    }
  };

  const confirmDelete = async () => {
    if (!projectToDelete) return;
    
    setDeletingId(projectToDelete.id);
    try {
      await deleteProject.mutateAsync(projectToDelete.id);
      toast.success('Projekt je uspješno obrisan');
      setDeleteModalOpen(false);
      setProjectToDelete(null);
    } catch (error) {
      toast.error('Greška pri brisanju projekta');
    } finally {
      setDeletingId(null);
    }
  };

  const cancelDelete = () => {
    setDeleteModalOpen(false);
    setProjectToDelete(null);
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  const handleOpenSettings = () => {
    navigate('/settings');
  };

  // Fetch thumbnail for a project (first scene's first shot)
  const fetchProjectThumbnail = async (projectId: string) => {
    if (projectThumbnails[projectId]) return projectThumbnails[projectId];

    try {
      const { data: scenes } = await supabase
        .from('scenes')
        .select('id')
        .eq('project_id', projectId)
        .order('order_index')
        .limit(1);

      if (!scenes || scenes.length === 0) return null;

      const firstScene = scenes[0];
      
      const { data: shots } = await supabase
        .from('shots')
        .select('thumbnail_url, media_url')
        .eq('scene_id', firstScene.id)
        .order('order_index')
        .limit(1);

      if (!shots || shots.length === 0) return null;

      const firstShot = shots[0];
      const thumbnailUrl = firstShot.thumbnail_url || firstShot.media_url;

      if (thumbnailUrl) {
        setProjectThumbnails(prev => ({ ...prev, [projectId]: thumbnailUrl }));
        return thumbnailUrl;
      }
    } catch (error) {
      console.error('Failed to fetch thumbnail:', error);
    }
    return null;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#030303] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
          <p className="text-slate-500 text-sm">Učitavanje projekata...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#030303] flex items-center justify-center p-4">
        <Card variant="glass" padding="lg" className="max-w-md text-center">
          <p className="text-red-400 mb-4">Greška pri učitavanju projekata</p>
          <Button onClick={() => window.location.reload()}>Pokušaj Ponovo</Button>
        </Card>
      </div>
    );
  }

  // Show Onboarding Wizard
  if (showOnboarding) {
    return (
      <OnboardingWizard
        onComplete={handleOnboardingComplete}
        onCancel={() => setShowOnboarding(false)}
        isGenerating={isGeneratingVideo}
        generationStatus={generationStatus}
      />
    );
  }

  return (
    <div className="min-h-screen bg-[#030303] p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-blue-600 rounded-xl">
              <Film size={28} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-black text-white uppercase tracking-tighter">
                MOJI <span className="text-blue-500">PROJEKTI</span>
              </h1>
              <p className="text-sm text-slate-500">
                {projects?.length || 0} {projects?.length === 1 ? 'projekt' : 'projekata'}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <Button
              variant="primary"
              onClick={handleCreateProject}
              isLoading={createProject.isPending}
              leftIcon={<Wand2 size={18} />}
            >
              Novi Video
            </Button>

            <Button
              variant="ghost"
              size="sm"
              onClick={handleOpenSettings}
              leftIcon={<Settings size={18} />}
              className="text-slate-400 hover:text-white"
            >
              Postavke
            </Button>

            <div className="flex items-center gap-2 ml-4 pl-4 border-l border-white/10">
              <div className="flex items-center gap-2 text-slate-400 text-sm">
                <User size={16} />
                <span className="hidden md:inline">{user?.email}</span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleSignOut}
                leftIcon={<LogOut size={16} />}
              >
                Odjavi se
              </Button>
            </div>
          </div>
        </div>

        {/* Projects Grid */}
        {projects && projects.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((project) => (
              <ProjectCard
                key={project.id}
                project={project}
                onSelect={() => handleSelectProject(project.id)}
                onDelete={() => handleDelete(project.id)}
                isDeleting={deletingId === project.id}
              />
            ))}
          </div>
        ) : (
          <EmptyState onCreateProject={handleCreateProject} />
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteModalOpen && projectToDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <Card variant="glass" padding="lg" className="max-w-md w-full">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center">
                <AlertTriangle size={24} className="text-red-500" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">Brisanje projekta</h3>
                <p className="text-sm text-slate-400">Ova akcija je nepovratna</p>
              </div>
            </div>
            
            <p className="text-slate-300 mb-6">
              Jeste li sigurni da želite obrisati projekt "<span className="text-white font-semibold">{projectToDelete.title}</span>"? 
              Sve scene, kadrovi i povezani podaci bit će trajno izgubljeni.
            </p>
            
            <div className="flex gap-3 justify-end">
              <Button
                variant="ghost"
                onClick={cancelDelete}
                disabled={deletingId === projectToDelete.id}
              >
                Odustani
              </Button>
              <Button
                variant="primary"
                onClick={confirmDelete}
                isLoading={deletingId === projectToDelete.id}
                className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
              >
                {deletingId === projectToDelete.id ? 'Brisanje...' : 'Obriši'}
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

// Project Card Component
interface ProjectCardProps {
  project: Project;
  onSelect: () => void;
  onDelete: () => void;
  isDeleting: boolean;
}

function ProjectCard({ project, onSelect, onDelete, isDeleting }: ProjectCardProps) {
  const [showMenu, setShowMenu] = useState(false);
  const [thumbnail, setThumbnail] = useState<string | null>(null);
  const [isLoadingThumbnail, setIsLoadingThumbnail] = useState(false);
  const menuRef = React.useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMenu]);

  // Fetch thumbnail when component mounts
  React.useEffect(() => {
    const loadThumbnail = async () => {
      setIsLoadingThumbnail(true);
      try {
        const { data: scenes } = await supabase
          .from('scenes')
          .select('id')
          .eq('project_id', project.id)
          .order('order_index')
          .limit(1);

        if (!scenes || scenes.length === 0) {
          setIsLoadingThumbnail(false);
          return;
        }

        const firstScene = scenes[0];
        
        const { data: shots } = await supabase
          .from('shots')
          .select('thumbnail_url, media_url')
          .eq('scene_id', firstScene.id)
          .order('order_index')
          .limit(1);

        if (shots && shots.length > 0) {
          const firstShot = shots[0];
          const thumbnailUrl = firstShot.thumbnail_url || firstShot.media_url;
          if (thumbnailUrl) {
            setThumbnail(thumbnailUrl);
          }
        }
      } catch (error) {
        console.error('Failed to fetch thumbnail:', error);
      } finally {
        setIsLoadingThumbnail(false);
      }
    };

    loadThumbnail();
  }, [project.id]);

  return (
    <Card
      variant="glass"
      padding="none"
      radius="2xl"
      className="group cursor-pointer overflow-hidden transition-all hover:scale-[1.02] hover:shadow-2xl"
      onClick={onSelect}
    >
      {/* Thumbnail */}
      <div className="aspect-video bg-gradient-to-br from-blue-900/50 to-purple-900/50 relative overflow-hidden">
        {isLoadingThumbnail ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <Loader2 size={32} className="text-white/20 animate-spin" />
          </div>
        ) : thumbnail ? (
          <img 
            src={thumbnail} 
            alt={`${project.title} thumbnail`}
            className="w-full h-full object-cover"
            onError={(e) => {
              // Fallback to placeholder if image fails to load
              e.currentTarget.style.display = 'none';
              const fallback = e.currentTarget.nextElementSibling as HTMLElement;
              if (fallback) {
                fallback.classList.remove('hidden');
                fallback.classList.add('flex', 'items-center', 'justify-center');
              }
            }}
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <Film size={48} className="text-white/20" />
          </div>
        )}
        
        {/* Fallback placeholder (hidden when image loads successfully) */}
        <div className="absolute inset-0 items-center justify-center hidden">
          <ImageIcon size={48} className="text-white/20" />
        </div>
        
        {/* Status Badge */}
        <div className="absolute top-3 left-3">
          <span className={`px-2 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wider ${
            project.status === 'mastered' 
              ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
              : project.status === 'production'
              ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
              : 'bg-slate-500/20 text-slate-400 border border-slate-500/30'
          }`}>
            {project.status}
          </span>
        </div>

        {/* Menu Button */}
        <div className="absolute top-3 right-3 z-50" ref={menuRef}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              setShowMenu(!showMenu);
            }}
            className="p-2 bg-black/50 rounded-lg text-white/70 hover:text-white hover:bg-black/70 transition-all"
          >
            <MoreVertical size={16} />
          </button>
          
          {/* Dropdown Menu */}
          {showMenu && (
            <div className="absolute top-full right-0 mt-2 w-40 bg-slate-800 rounded-xl border border-white/10 shadow-2xl py-1 z-60">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onDelete();
                  setShowMenu(false);
                }}
                disabled={isDeleting}
                className="w-full px-4 py-2 text-left text-sm text-red-400 hover:bg-white/5 flex items-center gap-2"
              >
                {isDeleting ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Trash2 size={14} />
                )}
                Obriši
              </button>
            </div>
          )}
        </div>

        {/* Play Button Overlay */}
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center shadow-lg transform scale-75 group-hover:scale-100 transition-transform">
            <Play size={24} className="text-white ml-1" />
          </div>
        </div>
      </div>

      {/* Info */}
      <div className="p-4">
        <h3 className="text-lg font-bold text-white mb-1 truncate">{project.title}</h3>
        <div className="flex items-center gap-4 text-xs text-slate-500">
          <span className="flex items-center gap-1">
            <Clock size={12} />
            {new Date(project.created_at).toLocaleDateString('hr-HR')}
          </span>
        </div>
      </div>
    </Card>
  );
}

// Empty State Component
interface EmptyStateProps {
  onCreateProject: () => void;
}

function EmptyState({ onCreateProject }: EmptyStateProps) {
  return (
    <div className="flex flex-col items-center justify-center py-20">
      <div className="w-24 h-24 bg-blue-600/10 rounded-full flex items-center justify-center mb-6">
        <Film size={48} className="text-blue-500" />
      </div>
      <h2 className="text-2xl font-bold text-white mb-2">Nemate projekata</h2>
      <p className="text-slate-500 mb-8 text-center max-w-md">
        Kreirajte svoj prvi video koristeći AI. Samo opišite što želite i AI će generirati kompletan video.
      </p>
      <Button
        variant="primary"
        size="lg"
        onClick={onCreateProject}
        leftIcon={<Wand2 size={20} />}
      >
        Kreiraj Prvi Video
      </Button>
    </div>
  );
}
